import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "@emotion/react";
import { withTranslation } from "react-i18next";
import { Col, Row } from "reactstrap";
import AddIcon from "@mui/icons-material/Add";
import styled from "@emotion/styled";
import { MuiChipsInput } from "mui-chips-input";
import { get } from "lodash";
import PropTypes from "prop-types";
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  Typography,
  // IconButton,
  // InputAdornment,
  // Tooltip,
} from "@mui/material";

// import { useRequestOTPMutation } from "apis/OTP";

// import { ReactComponent as UnverifiedMailIcon } from "assets/images/svg/unverified-mail.svg";
// import { ReactComponent as VerifiedMailIcon } from "assets/images/svg/verified-mail.svg";

import InputFieldText from "Components/Common/InputFieldText";
import { applicationTypes } from "common/constant";
import SelectField from "Components/Common/SelectField";
import Tooltip from "./Common/Tooltip";
import { useApplicantTypeQuery } from "apis/ApplicantType";
import { useLazyLocationByPincodeQuery } from "apis/Location";

import {
  addressOptions,
  getOptionValue,
  gstOptions,
  occupancyType,
  occupancyTypeForPower,
  otherUtilityApplicationOptions,
} from "common/enums";
import { onlyNumberKeys } from "utils";
// import VerifyEmailModal from "Components/Common/VerifyEmailModal";
// import { otpVerificationType } from "common/constant";

// const requestOTPType = otpVerificationType["Verification"];

const ApplicantDetails = ({
  formik,
  isEditable = false,
  isUtilityFieldRequired = false,
  isOtherUtilities = false,
  t,
}) => {
  const {
    values: {
      id,
      utilities = [],
      applicationTypeId,
      applicant: {
        applicantTypeId,
        pinCode,
        isGSTRegistered,
        alternateEmail,
        // isEmailVerified, email
      },
      inProgressApplicationId,
      isAllApplicationsProcessCompleted,
      payableBillAmount,
      consumerNumber,
      utilityConsumerId,
    },
    errors,
    setFieldValue,
  } = formik;
  const theme = useTheme();
  const [pinCodeError, setPinCodeError] = useState("");
  const ref = useRef();
  // const [isOTPSent, setIsOTPSent] = useState(false);
  // const [requestOTP, { isLoading: isRequestOTPLoading }] =
  //   useRequestOTPMutation();
  const StyledTextField = styled(MuiChipsInput)`
    label {
      width: "100%";
    }
    .MuiInputBase-root {
      padding: 5px 10px 0 0;
    }
  `;

  const occupancyTypes = useMemo(
    () => (isOtherUtilities ? occupancyType : occupancyTypeForPower),
    [isOtherUtilities]
  );

  const { data: { data: { items: applicantType } = {} } = {} } =
    useApplicantTypeQuery();

  const [getLocation] = useLazyLocationByPincodeQuery();

  const isIndividualApplicantType =
    applicantType?.find(
      (application) => application.name.toLowerCase() === "individual"
    ).id === applicantTypeId;

  const isDisable = useMemo(
    () =>
      (((![
        applicationTypes["Permanent Connection"],
        applicationTypes["Temporary Connection"],
      ].includes(applicationTypeId) &&
        inProgressApplicationId !== id &&
        !isAllApplicationsProcessCompleted) ||
        payableBillAmount > 0) &&
        !isEditable) ||
      (isOtherUtilities &&
        applicationTypeId === applicationTypes["Name Change/Transfer"] &&
        !utilityConsumerId),
    [
      inProgressApplicationId,
      id,
      isAllApplicationsProcessCompleted,
      payableBillAmount,
      applicationTypeId,
      isOtherUtilities,
      utilityConsumerId,
    ]
  );

  // const generateOTP = async (email) => {
  //   try {
  //     await requestOTP({
  //       username: email
  //       type: requestOTPType,
  //     });
  //     setIsOTPSent(true);
  //   } catch (error) {}
  // };

  useEffect(() => {
    (async () => {
      if (pinCode.toString().length === 6) {
        try {
          const res = await getLocation(pinCode);
          setPinCodeError("");
          setFieldValue("applicant.city", res.data.city);
        } catch (error) {
          setPinCodeError(t("Please enter valid pincode"));
          setFieldValue("applicant.city", "");
        }
      }
    })();
  }, [pinCode, getLocation, setFieldValue]);

  return (
    <>
      {/* {(isRequestOTPLoading || isOTPSent) && (
        <VerifyEmailModal
          isOpen={isOTPSent}
          handleClose={() => setIsOTPSent(false)}
          handleOTPverificationSuccess={() =>
            setFieldValue("applicant.isEmailVerified", true)
          }
          handleOTPVerificationFailed={() => {
            setFieldValue("applicant.isEmailVerified", false);
          }}
          email={email}
          isLoading={isRequestOTPLoading}
        />
      )} */}
      {isUtilityFieldRequired && (
        <Row>
          <Col xs={12} md={6} lg={4}>
            <SelectField
              label={t("Utilities")}
              name="utilities"
              multiple
              disableCloseOnSelect
              renderOption={(props, option, { selected }) => {
                return (
                  <li {...props} key={option.value}>
                    <Checkbox sx={{ marginRight: "8px" }} checked={selected} />
                    {option.label}
                  </li>
                );
              }}
              formik={formik}
              onChange={(event, options) => {
                if (event.key !== "Backspace" && event.key !== "Delete") {
                  setFieldValue("utilities", options);
                }
              }}
              value={utilities}
              options={otherUtilityApplicationOptions}
              placeholder="Select Utility"
            />
          </Col>
          <Col xs={12}>
            <Typography className="mb-3" lineHeight="1" variant="h6">
              {t("Applicant Details")}
            </Typography>
          </Col>
        </Row>
      )}
      <Row>
        <Col md={4} sm={6}>
          <SelectField
            label={t("Applicant Type") + "*"}
            name="applicant.applicantTypeId"
            formik={formik}
            disabled={isDisable}
            options={applicantType || []}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
          />
        </Col>
        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Applicant Name") + "*"}
            name="applicant.applicantName"
            formik={formik}
            disabled={isDisable}
            placeholder="Enter Applicant Name"
          />
        </Col>
        <Col md={4} sm={6}>
          <InputFieldText
            label={
              isOtherUtilities
                ? utilities.some(
                    ({ value }) =>
                      value === getOptionValue("utilityOptions", "DCS")
                  ) || !isIndividualApplicantType
                  ? t("PAN Number") + "*"
                  : t("PAN Number")
                : t("PAN Number") + "*"
            }
            name="applicant.panNumber"
            formik={formik}
            disabled={isDisable}
            onChange={(e) =>
              setFieldValue("applicant.panNumber", e.target.value.toUpperCase())
            }
            placeholder={
              isIndividualApplicantType
                ? t("PAN Number")
                : t("PAN Number") + "*"
            }
          />
        </Col>
        <Col md={4} sm={6}>
          <SelectField
            label={t("Occupancy Type") + "*"}
            name="applicant.occupancyTypeId"
            formik={formik}
            disabled={isDisable}
            options={occupancyTypes || []}
          />
        </Col>
        <Col md={4} sm={6}>
          <SelectField
            label={t("GST") + "*"}
            name="applicant.isGSTRegistered"
            onChange={(_, { value }) => {
              setFieldValue("applicant.isGSTRegistered", value);
              if (!value) {
                setFieldValue("applicant.gstNumber", "");
              }
            }}
            formik={formik}
            disabled={isDisable}
            options={gstOptions}
          />
        </Col>
        {isGSTRegistered && (
          <Col md={4} sm={6}>
            <InputFieldText
              label={t("GST Number") + "*"}
              name="applicant.gstNumber"
              formik={formik}
              onChange={(e) =>
                setFieldValue(
                  "applicant.gstNumber",
                  e.target.value.toUpperCase()
                )
              }
              placeholder="GST Number"
              disabled={isDisable}
            />
          </Col>
        )}
        <Col md={4} sm={6}>
          <SelectField
            label={t("Address Type") + "*"}
            name="applicant.addressType"
            formik={formik}
            options={addressOptions}
            disabled={isDisable}
          />
        </Col>
        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Correspondence Address") + "*"}
            name="applicant.correspondenceAddress"
            formik={formik}
            placeholder="Enter Correspondence Address"
            disabled={isDisable}
          />
        </Col>
        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Pincode") + "*"}
            name="applicant.pinCode"
            formik={formik}
            errorMessage={pinCodeError}
            type="number"
            InputProps={{
              inputProps: { min: 0, onKeyDown: onlyNumberKeys },
            }}
            placeholder="Enter Pincode"
            disabled={isDisable}
          />
        </Col>
        <Col md={4} sm={6}>
          <InputFieldText
            label={t("City") + "*"}
            name="applicant.city.name"
            formik={formik}
            placeholder="City"
            disabled
          />
        </Col>
        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Email") + "*"}
            name="applicant.email"
            formik={formik}
            type="email"
            placeholder="Enter Email"
            disabled={isDisable}
            // InputProps={{
            //   endAdornment: errors?.applicant?.email?.includes("OTP") ? (
            //     <InputAdornment position="end">
            //       <Tooltip title="Verify Email">
            //         <IconButton
            //           aria-label="toggle password visibility"
            //           onClick={() => {
            //             generateOTP(email);
            //           }}
            //           edge="end"
            //         >
            //           <UnverifiedMailIcon />
            //         </IconButton>
            //       </Tooltip>
            //     </InputAdornment>
            //   ) : isEmailVerified ? (
            //     <InputAdornment position="end">
            //       <VerifiedMailIcon />
            //     </InputAdornment>
            //   ) : (
            //     <></>
            //   ),
            // }}
            // onChange={(e) => {
            //   if (isEmailVerified) {
            //     setFieldValue("applicant.isEmailVerified", false);
            //   }
            //   setFieldValue("applicant.email", e.target.value);
            // }}
          />
        </Col>
        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Mobile Number") + "*"}
            name="applicant.mobileNumber"
            formik={formik}
            type="number"
            disabled={isDisable}
            InputProps={{
              inputProps: { min: 0, onKeyDown: onlyNumberKeys },
            }}
            placeholder={t("Enter Mobile Number")}
          />
        </Col>
        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Land Line")}
            type="number"
            InputProps={{
              inputProps: { min: 0, onKeyDown: onlyNumberKeys },
            }}
            name="applicant.landLine"
            formik={formik}
            placeholder="Enter Land-line Number"
            disabled={isDisable}
          />
        </Col>
        <Col md={4} sm={6}>
          <div className="mb-1">
            <StyledTextField
              className="w-100"
              inputRef={ref}
              disabled={isDisable}
              error={!isDisable && !!get(errors, "applicant.alternateEmail")}
              size="small"
              value={alternateEmail}
              label={t("Alternate Email")}
              name="applicant.alternateEmail"
              placeholder={t("Enter Alternate Email")}
              onChange={(value) => {
                setFieldValue("applicant.alternateEmail", value);
              }}
              clearInputOnBlur
              InputProps={{
                endAdornment: (
                  <Tooltip title="Add Email">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => {
                        setFieldValue("applicant.alternateEmail", [
                          ...alternateEmail,
                          ref.current?.value,
                        ]);
                      }}
                      disabled={isDisable}
                      edge="end"
                      color="primary"
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ),
              }}
            />
            <Typography
              variant="subtitle2"
              className={` ${
                !isDisable && !!get(errors, "applicant.alternateEmail")
                  ? "visible"
                  : "invisible"
              } input-field-error-message`}
              sx={{ color: theme.palette.error.main }}
            >
              {t(get(errors, "applicant.alternateEmail"))}
            </Typography>
          </div>
        </Col>
        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Alternate Mobile Number")}
            name="applicant.alternateMobileNumber"
            formik={formik}
            type="number"
            InputProps={{
              inputProps: { min: 0, onKeyDown: onlyNumberKeys },
            }}
            placeholder={t("Enter Alternate Mobile Number")}
            disabled={isDisable}
          />
        </Col>
      </Row>
      <Row>
        <Col md={4} sm={6}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={formik.values.applicant.eBillConfirmation}
                disabled
              />
            }
            label={t("E-Bill Confirmation")}
          />
        </Col>
      </Row>
      {!!inProgressApplicationId &&
      inProgressApplicationId !== id &&
      !isAllApplicationsProcessCompleted &&
      !!consumerNumber ? (
        <Typography variant="body2" color="error">
          {t(
            "There are some incomplete applications associated with this consumer number. You are unable to fill the form."
          )}
        </Typography>
      ) : payableBillAmount > 0 ? (
        <Typography variant="body2" color="error">
          You have an unpaid amount of {payableBillAmount} RS. Please pay first
          to continue.
        </Typography>
      ) : null}
    </>
  );
};

ApplicantDetails.propTypes = {
  formik: PropTypes.object,
  isEditable: PropTypes.bool,
  isUtilityFieldRequired: PropTypes.bool,
  isOtherUtilities: PropTypes.bool,
  t: PropTypes.func,
};
export default withTranslation()(ApplicantDetails);
